import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Columns3, Monitor, Moon, PanelBottom, Sun, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { userPreferencesApi, type UserPreferenceDto } from '@/api/userPreferences';
import { useAppStore, type ChatPosition } from './appStore';
import type { ThemePreference } from './theme';

type SettingsMenuProps = {
  /** Opens the full preferences dialog. Provided by TopBar to avoid import cycle. */
  onOpenPreferences?: () => void;
};

/**
 * Cog-button dropdown in the TopBar. Covers the lightweight settings users
 * flip frequently (theme + chat position). Full preferences dialog lives in #3.
 */
export function SettingsMenu({ onOpenPreferences }: SettingsMenuProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const chatPosition = useAppStore((s) => s.chatPosition);
  const theme = useAppStore((s) => s.theme);
  const setChatPosition = useAppStore((s) => s.setChatPosition);
  const setTheme = useAppStore((s) => s.setTheme);
  const themeMutation = useMutation({
    mutationFn: async (nextTheme: ThemePreference) => userPreferencesApi.upsert('ui.theme', nextTheme),
    onSuccess: (updatedTheme) => {
      queryClient.setQueryData<UserPreferenceDto[] | undefined>(['userPreferences'], (current) => {
        if (!current) {
          return [updatedTheme];
        }

        const next = current.map((preference) =>
          preference.key === 'ui.theme' ? updatedTheme : preference,
        );

        if (current.some((preference) => preference.key === 'ui.theme')) {
          return next;
        }

        return [...current, updatedTheme];
      });
    },
  });

  const applyThemePreference = (nextTheme: ThemePreference) => {
    setTheme(nextTheme);
    themeMutation.mutate(nextTheme);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('settings', { defaultValue: 'Settings' })}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <Settings size={16} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[220px] overflow-hidden rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-wb-md"
        >
          <DropdownMenu.Label className="px-2 py-1 text-xs uppercase text-muted-foreground">
            {t('settingsMenu.themeLabel', { defaultValue: 'Theme' })}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={theme}
            onValueChange={(value) => applyThemePreference(value as ThemePreference)}
          >
            <PreferenceItem
              value="system"
              label={t('settingsMenu.themeSystem', { defaultValue: 'System' })}
              icon={Monitor}
              active={theme === 'system'}
              testId="theme-system"
            />
            <PreferenceItem
              value="dark"
              label={t('settingsMenu.themeDark', { defaultValue: 'Dark' })}
              icon={Moon}
              active={theme === 'dark'}
              testId="theme-dark"
            />
            <PreferenceItem
              value="light"
              label={t('settingsMenu.themeLight', { defaultValue: 'Light' })}
              icon={Sun}
              active={theme === 'light'}
              testId="theme-light"
            />
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-2 py-1 text-xs uppercase text-muted-foreground">
            {t('settingsMenu.chatPositionLabel', { defaultValue: 'Chat position' })}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={chatPosition}
            onValueChange={(v) => setChatPosition(v as ChatPosition)}
          >
            <PreferenceItem
              value="right"
              label={t('settingsMenu.chatPositionRight', { defaultValue: 'Right rail' })}
              icon={Columns3}
              active={chatPosition === 'right'}
              testId="chat-position-right"
            />
            <PreferenceItem
              value="footer"
              label={t('settingsMenu.chatPositionFooter', { defaultValue: 'Footer drawer' })}
              icon={PanelBottom}
              active={chatPosition === 'footer'}
              testId="chat-position-footer"
            />
          </DropdownMenu.RadioGroup>
          {onOpenPreferences && (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={(e) => {
                  e.preventDefault();
                  onOpenPreferences();
                }}
                className="cursor-pointer rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted"
              >
                {t('settingsMenu.openPreferences', { defaultValue: 'All preferences…' })}
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

type PositionItemProps = {
  value: ChatPosition | ThemePreference;
  label: string;
  icon: LucideIcon;
  active: boolean;
  testId: string;
};

function PreferenceItem({ value, label, icon: Icon, active, testId }: PositionItemProps) {
  return (
    <DropdownMenu.RadioItem
      value={value}
      className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted data-[state=checked]:bg-muted"
      data-testid={testId}
    >
      <Icon size={14} className="text-muted-foreground" />
      <span className="flex-1">{label}</span>
      {active && <span className="text-xs text-primary">•</span>}
    </DropdownMenu.RadioItem>
  );
}
